import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Button from "@ats/src/components/shared/Button";
import breakpoint from "@shared/styles/breakpoints";

import { useRunPlatoCtaModals, RunPlatoCtaCardProps } from "./useRunPlatoCtaModals";
import PlatoSparkleIcon from "./PlatoSparkleIcon";

function RunPlatoCtaCardV2(props: RunPlatoCtaCardProps) {
  const { handleOnClickRunPlato } = useRunPlatoCtaModals(props);

  return (
    <Styled.Card>
      <Styled.Header>
        <Styled.Disc>
          <PlatoSparkleIcon />
        </Styled.Disc>
        <Styled.Title>Plato Reviews</Styled.Title>
      </Styled.Header>
      <Styled.Description>
        Plato βeta scores every candidate on role fit, relevant experience, skills, and gaps.
      </Styled.Description>
      <Styled.DescriptionShort>Plato βeta scores every candidate on role fit and skills.</Styled.DescriptionShort>
      <Styled.RunButton type="button" styleType="white" onClick={handleOnClickRunPlato}>
        <Styled.BtnTextFull>Run Plato</Styled.BtnTextFull>
        <Styled.BtnTextShort>Run</Styled.BtnTextShort>
      </Styled.RunButton>
    </Styled.Card>
  );
}

RunPlatoCtaCardV2.propTypes = {};
RunPlatoCtaCardV2.defaultProps = {};

export default RunPlatoCtaCardV2;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Card = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoCtaCardV2;
    margin: 0 8px 1rem;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.6875rem;
    padding: 1rem 10px;
    border-radius: 0.625rem;
    background: ${t.dark ? t.color.gray[800] : t.color.white};
    border: 1px solid ${t.dark ? 'transparent' : t.color.gray[200]};

    ${breakpoint.xxs} {
      padding-left: 0.875rem;
      padding-right: 0.875rem;
      margin-left: 0.75rem;
      margin-right: 0.75rem;
    }
    ${breakpoint.md} {
      padding-left: 10px;
      padding-right: 10px;
      margin-left: 8px;
      margin-right: 8px;
    }
    ${breakpoint.xl} {
      padding-left: 0.875rem;
      padding-right: 0.875rem;
      margin-left: 0.75rem;
      margin-right: 0.75rem;
    }
  `;
});

Styled.Header = styled.div`
  label: RunPlatoCtaCardV2_Header;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5625rem;

  ${breakpoint.xxs} {
    flex-direction: row;
  }
  ${breakpoint.md} {
    flex-direction: column;
  }
  ${breakpoint.xl} {
    flex-direction: row;
  }
`;

Styled.Disc = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoCtaCardV2_Disc;
    width: 1.875rem;
    height: 1.875rem;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    color: ${t.color.black};
    background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.07);

    svg {
      display: block;
      width: 1.0625rem;
      height: 1.0625rem;
    }
  `;
});

Styled.Title = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoCtaCardV2_Title;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${t.dark ? t.color.gray[200] : t.color.gray[900]};
    display: none;

    ${breakpoint.xxs} {
      display: block;
    }
    ${breakpoint.md} {
      display: none;
    }
    ${breakpoint.xl} {
      display: block;
    }
  `;
});

Styled.Description = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoCtaCardV2_Description;
    margin: 0;
    text-align: left;
    font-size: 0.71875rem;
    line-height: 1.4;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    display: none;

    ${breakpoint.xxs} {
      display: block;
    }
    ${breakpoint.md} {
      display: none;
    }
    ${breakpoint.xl} {
      display: block;
    }
  `;
});

Styled.DescriptionShort = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoCtaCardV2_DescriptionShort;
    margin: 0;
    text-align: center;
    font-size: 0.71875rem;
    line-height: 1.4;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    display: block;

    ${breakpoint.xxs} {
      display: none;
    }
    ${breakpoint.md} {
      display: block;
    }
    ${breakpoint.xl} {
      display: none;
    }
  `;
});

Styled.RunButton = styled(Button)((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoCtaCardV2_RunButton;
    width: 100% !important;
    max-width: none !important;
    cursor: pointer;
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]} !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    ${t.dark &&
    css`
      background-color: ${t.color.gray[700]} !important;
      color: ${t.color.gray[100]} !important;
      &:hover {
        background-color: ${t.color.gray[600]} !important;
        color: ${t.color.white} !important;
      }
    `}

    > span {
      overflow-x: visible;
      text-overflow: clip;
    }

    ${breakpoint.xxs} {
      max-width: 9.875rem !important;
    }
    ${breakpoint.md} {
      max-width: none !important;
    }
    ${breakpoint.lg} {
      max-width: 9.875rem !important;
    }
  `;
});

Styled.BtnTextFull = styled.span`
  display: none;

  ${breakpoint.xxs} {
    display: inline;
  }
  ${breakpoint.md} {
    display: none;
  }
  ${breakpoint.lg} {
    display: inline;
  }
`;

Styled.BtnTextShort = styled.span`
  display: inline;

  ${breakpoint.xxs} {
    display: none;
  }
  ${breakpoint.md} {
    display: inline;
  }
  ${breakpoint.lg} {
    display: none;
  }
`;
